/**
 * Algolia Dynamic Facet Configuration Manager
 * 
 * This module manages facet display configuration in Algolia, including:
 * - Facet eligibility and visibility
 * - Display order and presentation type
 * - Search term and collection-specific rules
 * - Fallback configuration
 * 
 * Integrates with Algolia's merchandising rules and widget customization.
 */

(function () {
  'use strict';

  // ==========================================
  // Facet Configuration
  // ==========================================

  /**
   * Default facet configuration (fallback)
   * Used when no custom rule applies
   */
  const DEFAULT_FACET_CONFIG = [
    {
      attribute: 'meta.filters.filter_product_category.fields.name',
      label: 'CATEGORY',
      type: 'list',
      enabled: true,
      order: 1,
      showCount: true,
      searchable: false,
    },
    {
      attribute: 'meta.filters.filter_product_type.fields.name',
      label: 'PRODUCT TYPE',
      type: 'list',
      enabled: true,
      order: 2,
      showCount: true,
      searchable: false,
    },
    {
      attribute: 'price_range',
      label: 'PRICE RANGE',
      type: 'list',
      enabled: true,
      order: 3,
      showCount: true,
      searchable: false,
    },
    {
      attribute: 'meta.netsuite.color',
      label: 'COLOR',
      type: 'swatch',
      enabled: true,
      order: 4,
      showCount: true,
      searchable: false,
    },
  ];

  /**
   * Phase 1 facet configurations
   */
  const PHASE_1_FACET_CONFIGS = [
    {
      attribute: 'facets.material',
      label: 'MATERIAL',
      type: 'list',
      enabled: true,
      order: 5,
      showCount: true,
      searchable: false,
      multiSelect: true,
    },
    {
      attribute: 'facets.availability_type',
      label: 'AVAILABILITY',
      type: 'list',
      enabled: true,
      order: 6,
      showCount: true,
      searchable: false,
      multiSelect: false,
    },
    {
      attribute: 'facets.room',
      label: 'ROOM',
      type: 'list',
      enabled: true,
      order: 7,
      showCount: true,
      searchable: false,
      multiSelect: true,
    },
    {
      attribute: 'facets.style',
      label: 'STYLE',
      type: 'list',
      enabled: true,
      order: 8,
      showCount: true,
      searchable: false,
      multiSelect: true,
    },
    {
      attribute: 'facets.collection',
      label: 'COLLECTION',
      type: 'list',
      enabled: true,
      order: 9,
      showCount: true,
      searchable: true,
      multiSelect: true,
    },
    {
      attribute: 'facets.size',
      label: 'SIZE',
      type: 'list',
      enabled: true,
      order: 10,
      showCount: true,
      searchable: false,
      multiSelect: true,
    },
  ];

  /**
   * Phase 2 facet configurations
   */
  const PHASE_2_FACET_CONFIGS = [
    {
      attribute: 'facets.shape',
      label: 'SHAPE',
      type: 'list',
      enabled: true,
      order: 11,
      showCount: true,
      categories: ['Rugs', 'Mirrors', 'Wall Decor', 'Dining Tables'],
    },
    {
      attribute: 'facets.product_features',
      label: 'FEATURES',
      type: 'list',
      enabled: true,
      order: 12,
      showCount: true,
      multiSelect: true,
      categories: ['Furniture', 'Tabletop'],
    },
    {
      attribute: 'facets.occasion',
      label: 'OCCASION',
      type: 'list',
      enabled: true,
      order: 13,
      showCount: true,
      multiSelect: true,
      categories: ['Tabletop', 'Holiday'],
    },
    {
      attribute: 'facets.pattern',
      label: 'PATTERN',
      type: 'list',
      enabled: true,
      order: 14,
      showCount: true,
      multiSelect: true,
      categories: ['Rugs', 'Bedding', 'Table Linens'],
    },
    {
      attribute: 'facets.finish',
      label: 'FINISH',
      type: 'list',
      enabled: true,
      order: 15,
      showCount: true,
      multiSelect: true,
      categories: ['Lighting', 'Wall Decor', 'Furniture'],
    },
  ];

  /**
   * Category-specific facet rules
   * Maps product categories to their relevant facets
   */
  const CATEGORY_FACET_RULES = {
    Furniture: {
      facets: [
        'meta.filters.filter_product_type.fields.name',
        'facets.material',
        'facets.availability_type',
        'facets.size',
        'facets.room',
        'facets.style',
        'facets.product_features',
        'facets.finish',
        'price_range',
        'meta.netsuite.color',
      ],
      collapsedByDefault: ['facets.finish'],
    },
    Tabletop: {
      facets: [
        'meta.filters.filter_product_type.fields.name',
        'facets.collection',
        'facets.material',
        'facets.occasion',
        'facets.product_features',
        'price_range',
        'meta.netsuite.color',
      ],
      collapsedByDefault: [],
    },
    Rugs: {
      facets: [
        'facets.size',
        'facets.material',
        'facets.room',
        'facets.shape',
        'facets.pattern',
        'facets.style',
        'price_range',
        'meta.netsuite.color',
      ],
      collapsedByDefault: ['facets.pattern'],
    },
    'Wall Decor': {
      facets: [
        'meta.filters.filter_product_type.fields.name',
        'facets.room',
        'facets.shape',
        'facets.finish',
        'facets.style',
        'price_range',
      ],
      collapsedByDefault: [],
    },
    'Wall Art': {
      facets: [
        'facets.subject_matter',
        'facets.orientation',
        'facets.room',
        'facets.style',
        'price_range',
      ],
      collapsedByDefault: [],
    },
  };

  // ==========================================
  // Configuration Manager
  // ==========================================

  /**
   * Get facet configuration for a collection or search term
   * @param {String} collectionHandle - Shopify collection handle
   * @param {String} searchQuery - Search query text
   * @returns {Array} - Ordered array of facet configurations
   */
  function getFacetConfig(collectionHandle, searchQuery) {
    // Check for collection-specific rules
    if (collectionHandle) {
      const categoryRule = getCategoryRuleForCollection(collectionHandle);
      if (categoryRule) {
        return buildFacetConfigFromRule(categoryRule);
      }
    }

    // Check for search-term-specific rules
    if (searchQuery) {
      const searchRule = getSearchTermRule(searchQuery);
      if (searchRule) {
        return buildFacetConfigFromRule(searchRule);
      }
    }

    // Return default configuration
    return getDefaultFacetConfig();
  }

  /**
   * Get category rule for collection
   */
  function getCategoryRuleForCollection(collectionHandle) {
    // Map collection handles to categories
    const categoryMap = {
      furniture: 'Furniture',
      'living-room-furniture': 'Furniture',
      'dining-room-furniture': 'Furniture',
      'bedroom-furniture': 'Furniture',
      tabletop: 'Tabletop',
      dinnerware: 'Tabletop',
      drinkware: 'Tabletop',
      rugs: 'Rugs',
      'area-rugs': 'Rugs',
      'wall-decor': 'Wall Decor',
      'wall-art': 'Wall Art',
    };

    const category = categoryMap[collectionHandle];
    return category ? CATEGORY_FACET_RULES[category] : null;
  }

  /**
   * Get search term rule
   */
  function getSearchTermRule(searchQuery) {
    const query = searchQuery.toLowerCase().trim();

    // Furniture-related searches
    if (
      query.includes('sofa') ||
      query.includes('chair') ||
      query.includes('table') ||
      query.includes('bed')
    ) {
      return CATEGORY_FACET_RULES.Furniture;
    }

    // Rug-related searches
    if (query.includes('rug') || query.includes('carpet')) {
      return CATEGORY_FACET_RULES.Rugs;
    }

    // Tabletop-related searches
    if (
      query.includes('plate') ||
      query.includes('bowl') ||
      query.includes('glass') ||
      query.includes('mug')
    ) {
      return CATEGORY_FACET_RULES.Tabletop;
    }

    return null;
  }

  /**
   * Build facet configuration from rule
   */
  function buildFacetConfigFromRule(rule) {
    const allConfigs = [
      ...DEFAULT_FACET_CONFIG,
      ...PHASE_1_FACET_CONFIGS,
      ...PHASE_2_FACET_CONFIGS,
    ];

    // Filter to only include facets in the rule
    const configMap = new Map(
      allConfigs.map((config) => [config.attribute, config])
    );

    const orderedConfigs = rule.facets
      .map((attr) => configMap.get(attr))
      .filter((config) => config && config.enabled);

    // Apply rule-specific settings
    if (rule.collapsedByDefault) {
      orderedConfigs.forEach((config) => {
        if (rule.collapsedByDefault.includes(config.attribute)) {
          config.defaultCollapsed = true;
        }
      });
    }

    return orderedConfigs;
  }

  /**
   * Get default facet configuration
   */
  function getDefaultFacetConfig() {
    return [
      ...DEFAULT_FACET_CONFIG,
      ...PHASE_1_FACET_CONFIGS.filter((config) => config.enabled),
    ].sort((a, b) => a.order - b.order);
  }

  /**
   * Check if facet should be displayed
   * Facets with no values or only one value (when not meaningful) should be hidden
   */
  function shouldDisplayFacet(facetData, facetConfig) {
    if (!facetData || !facetConfig.enabled) return false;

    const valueCount = Object.keys(facetData).length;

    // Hide empty facets
    if (valueCount === 0) return false;

    // Hide single-value facets that don't provide meaningful filtering
    if (valueCount === 1 && !facetConfig.showSingleValue) {
      return false;
    }

    return true;
  }

  /**
   * Get facet widget type
   */
  function getFacetWidgetType(facetConfig) {
    // Determine widget type based on configuration
    switch (facetConfig.type) {
      case 'swatch':
        return 'color-swatch';
      case 'range':
        return 'range-slider';
      case 'toggle':
        return 'toggle';
      case 'list':
      default:
        return 'checkbox-list';
    }
  }

  // ==========================================
  // Export to Global Scope
  // ==========================================

  window.AlgoliaFacetConfig = {
    getFacetConfig,
    shouldDisplayFacet,
    getFacetWidgetType,
    DEFAULT_FACET_CONFIG,
    PHASE_1_FACET_CONFIGS,
    PHASE_2_FACET_CONFIGS,
    CATEGORY_FACET_RULES,
  };
})();
